import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, FiltersSection, LoadingState, PayoutItemsAgGridTable } from '../';
import { useAgGridColumns, usePayoutItems } from '../../hooks';

export const Main = () => {
  const { t } = useTranslation('codPayoutItems');

  // 🎯 Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    items,
    meta,
    isLoading,
    isFetching,
    error,
    refetch,
    filters,
    updateFilters,
    resetFilters,
    handleSortingChange,
    hasFilters,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePayoutItems();

  const { columnDefs, getColumnsInOrder } = useAgGridColumns();

  // Mapeo inverso: campos de API a campos de AG-Grid
  // Solo amount y created_at son ordenables según los requisitos
  const API_TO_FIELD_SORT: Record<string, string> = {
    amount: 'amount',
    created_at: 'created_at',
  };

  // Obtener sorting actual desde los filtros y mapear a campos de AG-Grid
  const sorting = useMemo(() => {
    if (filters['sorting[column]'] && filters['sorting[direction]']) {
      const apiField = filters['sorting[column]'];
      const agGridField = API_TO_FIELD_SORT[apiField] || apiField;
      return {
        field: agGridField,
        direction: filters['sorting[direction]'] as 'asc' | 'desc',
      };
    }
    return null;
  }, [filters]);

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Configurar breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.payouts', 'Liquidaciones COD'), path: '/cod-payments/payouts' },
      { label: t('breadcrumb.all', 'Todos') },
    ],
    [t],
  );

  // Establecer breadcrumbs en la topbar
  usePageNavigation({
    breadcrumbs,
  });

  // Obtener columnas para exportar
  const exportColumns = useMemo(() => getColumnsInOrder(), [getColumnsInOrder]);

  // Total de items para detectar estado vacío
  const total = meta?.total ?? 0;

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.items', 'Cargando items...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header con filtros */}
      <div className="mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <FiltersSection
            filters={filters}
            onFiltersChange={updateFilters}
            onResetFilters={resetFilters}
            isFetching={isFetching}
            exportColumns={exportColumns}
          />
        </div>
      </div>

      {/* 🎯 Estado vacío: siempre en el DOM, controlado por opacity y height */}
      <div
        className={`transition-opacity duration-200 overflow-hidden ${
          total === 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0 pointer-events-none'
        }`}
      >
        <EmptyView
          type={hasFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          title={
            hasFilters ? t('empty.no_results_title', 'No se encontraron resultados') : t('empty.title', 'No hay items')
          }
          description={
            hasFilters
              ? t(
                  'empty.no_results_description',
                  'No hay items que coincidan con los filtros aplicados. Intenta ajustar los filtros.',
                )
              : t('empty.description', 'No hay items de liquidación disponibles')
          }
        />
      </div>

      {/* 🎯 Vista de items: siempre en el DOM, controlado por opacity y height */}
      <div
        className={`mb-4 transition-opacity duration-200 overflow-hidden ${
          total > 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0'
        }`}
      >
        <PayoutItemsAgGridTable
          items={items}
          columnDefs={columnDefs}
          isLoading={isLoading}
          isFetching={isFetching}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      {/* Scroll infinito: se carga más al hacer scroll en la tabla */}
    </div>
  );
};
